import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpHeaders,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, catchError, concatMap, delay, iif, of, retryWhen, switchMap, throwError } from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';
import { environment } from 'src/environments/environment';

export interface RetryRequestOptions {
  maximumRetries: number;
  retryDelay: number;
}

@Injectable()
export class HttpAuthInterceptor implements HttpInterceptor {
  // default retry options
  private retryRequestOptions: RetryRequestOptions = {
    maximumRetries: 2,
    retryDelay: 200, //milliseconds
  };
  private retryOptionConfigured = false;

  constructor(private authenticationService: AuthenticationService) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isApiUrl = request.url.startsWith(environment.api_url);
    if (!isApiUrl || this.isRotaSemRenovacao(request.url)) return next.handle(request).pipe(this.retryPipe(request));

    const tokenUsado = this.tokenAtual();

    // 401 de rota protegida: uma renovação coordenada e uma única repetição.
    return this.enviar(request, next, tokenUsado).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.status !== 401) return throwError(() => err);

        return this.authenticationService.renovarToken(tokenUsado).pipe(
          catchError(erroRefresh => {
            if (!this.authenticationService.isSessaoEncerrada(erroRefresh)) return throwError(() => erroRefresh);
            this.authenticationService.encerrarAutenticacao();
            return throwError(() => err);
          }),
          switchMap(novoToken => this.enviar(request, next, novoToken).pipe(
            catchError((erroRepetido: HttpErrorResponse) => {
              // A única repetição também recebeu 401: autenticação terminal, sem novo refresh.
              if (erroRepetido?.status === 401) this.authenticationService.encerrarAutenticacao();
              return throwError(() => erroRepetido);
            })
          ))
        );
      })
    );

    // first time it run: need to replace default retry config options
    // if (!this.retryOptionConfigured) {
    //   let appConfig = this.appConfigService.getConfig();
    //   if (appConfig) {
    //     this.retryRequestOptions = {
    //       maximumRetries: appConfig.maximumRetries,
    //       retryDelay: appConfig.retryDelay,
    //     };
    //     this.retryOptionConfigured = true;
    //   }
    // }


    // return next.handle(request).pipe(
    //   catchError((err: HttpErrorResponse) => {
    //     if (err) {
    //       switch (err.status) {
    //         case 401:
    //           this.authenticationService.tokenExpiradoEmit(true);
    //           break;
    //         default:
    //           break;
    //       }
    //     }
    //     throw err;
    //   })
    // );
  }

  private tokenAtual(): string | null {
    const token = this.authenticationService.obterTokenUsuario;
    return token && token !== 'null' ? token : null;
  }

  private enviar(request: HttpRequest<unknown>, next: HttpHandler, token: string | null): Observable<HttpEvent<unknown>> {
    const req = token ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : request;
    return next.handle(req).pipe(this.retryPipe(request));
  }

  private isRotaSemRenovacao(url: string): boolean {
    return /\/(login|token\/refresh|token\/logout)(\?|$)/.test(url);
  }

  // Só repete GET: gravações (POST/PUT/PATCH/DELETE) não podem ser duplicadas.
  retryPipe<T>(request: HttpRequest<unknown>) {
    return retryWhen<T>((errors: Observable<HttpErrorResponse>) =>
      errors.pipe(
        // Use concat map to keep the errors in order and make sure they
        // aren't executed in parallel
        concatMap((error: HttpErrorResponse, retryId: number) =>
          // Executes a conditional Observable depending on the result
          // of the first argument
          iif(
            () => retryId >= this.retryRequestOptions.maximumRetries || request.method !== 'GET' ||
              (error.status <= 500 && !(error.url ?? '').includes('/v1/clientes') && !(error.url ?? '').includes('/v1/lojas')),
            // If the condition is true we throw the error (the last error)
            this.returnError(error),
            // Otherwise we pipe this back into our stream and delay the retry
            this.retryError(error, retryId, this.retryRequestOptions.retryDelay),
          ),
        ),
      ),
    );
  }
  returnError(err: HttpErrorResponse) {
    // 401 é tratado em intercept(): renova o token e só então avisa sessão expirada.
    // throw err;
    return throwError(err);
  }

  retryError(error: HttpErrorResponse, retryId: number, retryDelay: number) {
    console.log('retry', error.url, retryDelay, retryId)
    // retry request with increasing delay between each attemp
    return of(error).pipe(delay(retryDelay * retryId));
  }
}
